<template>
    <Head title="Notification Preferences" />
    <div
        class="min-h-screen bg-gray-100 dark:bg-slate-900 flex flex-col items-center py-12 px-4 md:px-8"
    >
        <div
            class="max-w-md w-full space-y-8 bg-white dark:bg-slate-800 p-6 rounded-lg shadow-md dark:shadow-slate-900/50"
        >
            <div>
                <h2
                    class="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-gray-100"
                >
                    Notification Preferences
                </h2>
                <p class="mt-2 text-center text-sm text-gray-600 dark:text-gray-300">
                    Manage your notification settings
                </p>
            </div>

            <div v-if="loading" class="flex justify-center items-center p-8">
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"
                ></div>
            </div>

            <div
                v-else-if="preferences && preferences.length > 0"
                class="space-y-4"
            >
                <div
                    v-for="pref in preferences"
                    :key="pref.name"
                    class="p-4 border border-gray-200 dark:border-slate-700 rounded-md"
                >
                    <div class="flex items-center justify-between">
                        <div>
                            <h4 class="font-semibold dark:text-gray-100">{{ pref.title }}</h4>
                            <p class="text-sm text-gray-600 dark:text-gray-300">
                                {{ pref.description }}
                            </p>
                        </div>
                        <div class="flex items-center">
                            <label
                                class="relative inline-flex items-center cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    :checked="pref.value === 'Y'"
                                    class="sr-only peer"
                                    @change="
                                        updatePreference(
                                            pref.name,
                                            $event.target.checked ? 'Y' : 'N'
                                        )
                                    "
                                />
                                <div
                                    class="w-11 h-6 bg-gray-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-slate-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"
                                ></div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="text-center p-8">
                <p class="text-gray-500 dark:text-gray-400">
                    No notification preferences available.
                </p>
            </div>

            <div class="mt-6">
                <Link
                    href="/"
                    class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    Return to Homepage
                </Link>
            </div>
        </div>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { ref, onMounted } from 'vue';

const preferences = ref([]);
const loading = ref(true);

const getPrefToken = () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    return urlParams.get('p') ? urlParams.get('p') : '';
};

const fetchPreferences = async () => {
    try {
        loading.value = true;
        const response = await window.axios.get(
            '/api/user-settings/get-notification-preferences?p=' +
                getPrefToken()
        );
        preferences.value = response.data.preferences;
    } catch (error) {
        console.error('Error fetching notification preferences:', error);
    } finally {
        loading.value = false;
    }
};

const updatePreference = async (prefName, value) => {
    try {
        await window.axios.put(
            `/api/user-settings/notification-preferences/${prefName}?p=${getPrefToken()}`,
            {
                value: value,
            }
        );

        // Update local state
        const prefIndex = preferences.value.findIndex(
            (p) => p.name === prefName
        );
        if (prefIndex !== -1) {
            preferences.value[prefIndex].value = value;
        }
    } catch (error) {
        console.error('Error updating preference:', error);
        // Revert the UI change if the API call fails
        const prefIndex = preferences.value.findIndex(
            (p) => p.name === prefName
        );
        if (prefIndex !== -1) {
            preferences.value[prefIndex].value =
                preferences.value[prefIndex].value === 'Y' ? 'N' : 'Y';
        }
    }
};

onMounted(() => {
    fetchPreferences();
});

defineOptions({
    layout: null, // No layout for this page as it's for non-authenticated users
});
</script>
